import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import { AxiosResponse } from 'axios';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { FiPlus } from 'react-icons/fi';

import { PageLoading } from '@vyce/core/src/components';
import { AppButton } from '@vyce/core/src/components/AppButton';
import { AppSwitch } from '@vyce/core/src/components/inputs';
import { SettingsCard } from '@vyce/core/src/components/SettingsCard';
import { Team } from '@vyce/core/src/types';
import { getStaffRequest } from '@vyce/core/src/api/companies';
import {
  ExpiringDocumentsSettingPayload,
  ExpiringDocumentsSettings,
  ExpiringDocumentsSettingsListResponse,
} from '@vyce/core/src/api/types';

import { useStaffSearch } from '../../../time/TimeNotifications/hooks/useStaffSearch';
import { TEAMS_RECIPIENT_ROLES } from '../constants';
import { useExpiringDocumentsSettings } from '../hooks';
import { useStyles } from '../styles';
import { EMPTY_FORM_VALUE, ExpiringDocumentsFormValue, ExpiringDocumentsFormValues } from '../types';
import { ExpiringDocumentsCard } from './ExpiringDocumentsCard';

interface Props {
  companyId?: string;
  teams?: Team[];
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  getSettings: (companyId: string) => Promise<AxiosResponse<ExpiringDocumentsSettingsListResponse>>;
  createSetting: (
    companyId: string,
    payload: ExpiringDocumentsSettingPayload
  ) => Promise<AxiosResponse<ExpiringDocumentsSettings>>;
  updateSetting: (
    companyId: string,
    settingId: string,
    payload: ExpiringDocumentsSettingPayload
  ) => Promise<AxiosResponse<ExpiringDocumentsSettings>>;
  deleteSetting: (companyId: string, settingId: string) => Promise<AxiosResponse<unknown>>;
  getStaff?: typeof getStaffRequest;
}

/**
 * The "Document Expiry Alerts" tile: one settings card holding every alert the company has
 * set up, the same way Time & Attendance holds its Snapshot Emails in a single tile.
 */
export const DocumentExpiryAlerts: React.FC<Props> = ({
  companyId,
  teams = [],
  expanded,
  onExpandedChange,
  getSettings,
  createSetting,
  updateSetting,
  deleteSetting,
  getStaff,
}) => {
  const classes = useStyles();
  // Keyed by the field-array row id so the open/closed state survives adds and deletes.
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const openNextRef = useRef(false);
  const allTeamIds = useMemo(() => teams.map(t => t.uuid), [teams]);

  const { staff, setSubstring: setStaffSubstring } = useStaffSearch({
    companyId,
    roles: TEAMS_RECIPIENT_ROLES,
    getStaff,
  });

  const methods = useForm<ExpiringDocumentsFormValues>({
    defaultValues: { notifications: [] },
  });

  const { fields, append, remove } = useFieldArray({ control: methods.control, name: 'notifications' });

  const { loading, savingIndex, deletingIndex, saveNotification, deleteNotification, toggleNotification } =
    useExpiringDocumentsSettings({
      companyId,
      allTeamIds,
      methods,
      remove,
      getSettings,
      createSetting,
      updateSetting,
      deleteSetting,
      getStaff,
    });

  const notifications: ExpiringDocumentsFormValue[] = methods.watch('notifications') || [];
  // Only alerts that exist on the server count towards the tile status — a row added but not
  // yet saved is nothing scheduled.
  const savedAlerts = notifications.filter(alert => !!alert?.uuid);
  const activeCount = savedAlerts.filter(alert => alert?.enabled !== false).length;
  const allActive = savedAlerts.length > 0 && activeCount === savedAlerts.length;

  const addNotification = () => {
    openNextRef.current = true;
    append({ ...EMPTY_FORM_VALUE, teamIds: allTeamIds });
  };

  // Adding an alert opens only the row that was just appended; everything else keeps its state.
  useEffect(() => {
    if (!openNextRef.current) return;
    openNextRef.current = false;
    const added = fields[fields.length - 1];
    if (added) setExpandedIds(prev => ({ ...prev, [added.id]: true }));
  }, [fields]);

  const setExpanded = (id: string, isExpanded: boolean) =>
    setExpandedIds(prev => ({ ...prev, [id]: isExpanded }));

  const toggleAll = (enabled: boolean) => {
    notifications.forEach((alert, index) => {
      if (alert?.uuid && (alert.enabled ?? false) !== enabled) toggleNotification(index, enabled);
    });
  };

  const status = () => {
    if (!savedAlerts.length) return 'Not set up yet';
    if (!activeCount) return 'Off';
    return `On · ${activeCount} ${activeCount === 1 ? 'alert' : 'alerts'}`;
  };

  return (
    <FormProvider {...methods}>
      <SettingsCard
        title="Document Expiry Alerts"
        description="A scheduled email listing everyone whose Right to Work, ID or qualifications are about to expire or already have. Set up as many as you need — each one has its own teams, documents, timing and recipients."
        status={status()}
        statusTone={savedAlerts.length ? (activeCount ? 'on' : 'off') : 'info'}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        keepMounted
        headerControl={
          <AppSwitch
            checked={allActive}
            disabled={!savedAlerts.length}
            onChange={(_, checked) => toggleAll(checked)}
            inputProps={{ 'aria-label': 'Document Expiry Alerts' }}
          />
        }
      >
        {loading ? (
          <Box height="100px">
            <PageLoading />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gridGap={12}>
            {fields.map((field, index) => (
              <ExpiringDocumentsCard
                key={field.id}
                index={index}
                teams={teams}
                staff={staff}
                setStaffSubstring={setStaffSubstring}
                expanded={!!expandedIds[field.id]}
                onExpandedChange={isExpanded => setExpanded(field.id, isExpanded)}
                onSave={saveNotification}
                onDelete={deleteNotification}
                onToggle={toggleNotification}
                savingIndex={savingIndex}
                deletingIndex={deletingIndex}
                boldClassName={classes.bold}
              />
            ))}

            {!fields.length && (
              <Typography variant="caption" color="textSecondary">
                No alerts yet. Add one to start emailing document-expiry summaries.
              </Typography>
            )}

            <Box display="flex">
              <AppButton
                variant="contained"
                color="primary"
                startIcon={<FiPlus size={16} />}
                onClick={addNotification}
                disabled={!companyId}
              >
                Add an alert
              </AppButton>
            </Box>
          </Box>
        )}
      </SettingsCard>
    </FormProvider>
  );
};
